export interface CountryInfo {
  code: string;
  name: string;
  flag: string;
}

export interface GeolocationData {
  country: CountryInfo;
  city?: string;
  region?: string;
  ip?: string;
}

class GeolocationService {
  private cache: GeolocationData | null = null;
  private readonly fallbackCountry: CountryInfo = {
    code: 'UN',
    name: 'Unknown',
    flag: '🌍'
  };

  async getUserLocation(): Promise<GeolocationData> {
    // Return cached result if available
    if (this.cache) {
      return this.cache;
    }

    try {
      // Try ipapi.co first (free tier: 1000 requests/day)
      const response = await fetch('https://ipapi.co/json/');

      if (!response.ok) {
        throw new Error('Primary geolocation service failed');
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.reason || 'Geolocation service error');
      }

      const locationData: GeolocationData = {
        country: {
          code: data.country_code || 'UN',
          name: data.country_name || 'Unknown',
          flag: this.getCountryFlag(data.country_code)
        },
        city: data.city,
        region: data.region,
        ip: data.ip
      };

      this.cache = locationData;
      return locationData;
    } catch (error) {
      console.warn('Primary geolocation failed, trying fallback:', error);

      try {
        // Fallback to a different free service
        const fallbackResponse = await fetch('https://api.country.is/');

        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          const locationData: GeolocationData = {
            country: {
              code: fallbackData.country || 'UN',
              name: this.getCountryInfo(fallbackData.country).name,
              flag: this.getCountryFlag(fallbackData.country)
            },
            ip: fallbackData.ip
          };

          this.cache = locationData;
          return locationData;
        }
      } catch (fallbackError) {
        console.warn('Fallback geolocation also failed:', fallbackError);
      }

      // Return fallback data
      const fallbackData: GeolocationData = {
        country: this.fallbackCountry
      };

      this.cache = fallbackData;
      return fallbackData;
    }
  }

  getCountryFlag(countryCode?: string): string {
    if (!countryCode || countryCode.length !== 2) {
      return '🌍';
    }

    // Convert country code to flag emoji
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));

    return String.fromCodePoint(...codePoints);
  }

  clearCache(): void {
    this.cache = null;
  }

  // Get country info from country code
  getCountryInfo(countryCode: string): CountryInfo {
    const countryNames: Record<string, string> = {
      'US': 'United States',
      'CA': 'Canada',
      'GB': 'United Kingdom',
      'DE': 'Germany',
      'FR': 'France',
      'JP': 'Japan',
      'CN': 'China',
      'IN': 'India',
      'BR': 'Brazil',
      'AU': 'Australia',
      'MX': 'Mexico',
      'IT': 'Italy',
      'ES': 'Spain',
      'RU': 'Russia',
      'KR': 'South Korea',
      'NL': 'Netherlands',
      'SE': 'Sweden',
      'NO': 'Norway',
      'DK': 'Denmark',
      'FI': 'Finland',
      'CH': 'Switzerland',
      'AT': 'Austria',
      'BE': 'Belgium',
      'IE': 'Ireland',
      'PL': 'Poland',
      'TR': 'Turkey',
      'GR': 'Greece',
      'PT': 'Portugal',
      'CZ': 'Czech Republic',
      'HU': 'Hungary',
      'RO': 'Romania',
      'BG': 'Bulgaria',
      'HR': 'Croatia',
      'SK': 'Slovakia',
      'SI': 'Slovenia',
      'EE': 'Estonia',
      'LV': 'Latvia',
      'LT': 'Lithuania',
      'LU': 'Luxembourg',
      'MT': 'Malta',
      'CY': 'Cyprus',
      'IS': 'Iceland',
      'LI': 'Liechtenstein',
      'MC': 'Monaco',
      'SM': 'San Marino',
      'VA': 'Vatican City',
      'AD': 'Andorra'
    };

    return {
      code: countryCode.toUpperCase(),
      name: countryNames[countryCode.toUpperCase()] || countryCode.toUpperCase(),
      flag: this.getCountryFlag(countryCode)
    };
  }
}

export const geolocationService = new GeolocationService();